// src/pages/SettingsPage.tsx
// プロフィール設定ページ（初回設定・編集兼用）
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, getDoc, updateDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../services/firebase';
import { uploadAvatar } from '../services/avatarService';
import { updateProfile } from 'firebase/auth';
import { CameraIcon, ArrowLeftIcon, LogOutIcon } from 'lucide-react';
import logoImage from '../assets/images/UI_orifoli_logo_black.png';
import CropImageModal from '../components/common/CropImageModal';
import {
  sanitizeInput,
  displayNameSanitizeOptions,
  bioSanitizeOptions,
  usernameSanitizeOptions
} from '../utils/inputSanitizer';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation('settings');
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [avatarBlob, setAvatarBlob] = useState<Blob | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isProfileExisting, setIsProfileExisting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAvatarConfirm, setShowAvatarConfirm] = useState(false);

  // Crop modal state
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const MAX_DISPLAY_NAME_LENGTH = 25;
  const MAX_BIO_LENGTH = 150;

  // 初期化
  useEffect(() => {
    const initializePage = async () => {
      if (authLoading) return;

      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setIsProfileExisting(true);
          setDisplayName(userData.displayName || userData.xDisplayName || '');
          setUsername(userData.username || userData.xUsername || '');
          setBio(userData.bio || '');

          if (userData.avatarUrl) {
            setAvatarPreview(userData.avatarUrl);
          } else if (userData.xPhotoURL) {
            setAvatarPreview(userData.xPhotoURL);
          }
        } else {
          // 新規ユーザー: Firebase AuthやX認証の情報を使用
          if (user.displayName) {
            setDisplayName(user.displayName);
          }
          if (user.photoURL) {
            setAvatarPreview(user.photoURL);
          }
          if (user.email) {
            const emailUsername = user.email.split('@')[0];
            const sanitizedUsername = emailUsername.replace(/[^a-zA-Z0-9_]/g, '');
            setUsername(sanitizedUsername);
          }
        }
      } catch (error) {
        console.error('Error checking existing profile:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializePage();
  }, [user, authLoading, navigate]);

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.size > 10 * 1024 * 1024) {
        setError(t('errors.fileTooLarge'));
        return;
      }

      if (!file.type.match('image/*')) {
        setError(t('errors.invalidFileType'));
        return;
      }

      // Open crop modal with the selected image
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropImageSrc(reader.result as string);
        setIsCropModalOpen(true);
      };
      reader.readAsDataURL(file);

      // Reset file input for re-selection of same file
      e.target.value = '';
    }
  };

  const handleCropComplete = (croppedBlob: Blob) => {
    setAvatarBlob(croppedBlob);
    // Create preview URL from blob
    const previewUrl = URL.createObjectURL(croppedBlob);
    setAvatarPreview(previewUrl);
    setIsCropModalOpen(false);
    setCropImageSrc(null);
    setShowAvatarConfirm(false);
  };

  const handleCropModalClose = () => {
    setIsCropModalOpen(false);
    setCropImageSrc(null);
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadAvatar = async (): Promise<string | null> => {
    if (!avatarBlob || !user) return null;

    try {
      // バックエンドAPI経由でGCSにアップロード
      const avatarUrl = await uploadAvatar(avatarBlob);
      return avatarUrl;
    } catch (error) {
      console.error('Error uploading avatar:', error);
      throw error;
    }
  };

  const isUsernameUnique = async (checkUsername: string): Promise<boolean> => {
    if (isProfileExisting) {
      const userDocRef = doc(db, 'users', user!.uid);
      const userDoc = await getDoc(userDocRef);
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.username === checkUsername || userData.xUsername === checkUsername) {
          return true;
        }
      }
    }

    try {
      const usernameDocRef = doc(db, 'users_by_username', checkUsername);
      const usernameDoc = await getDoc(usernameDocRef);
      if (!usernameDoc.exists()) {
        return true;
      }
      const entryData = usernameDoc.data();
      return entryData.uid === user!.uid;
    } catch (error) {
      console.error('Error checking username uniqueness:', error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!user) {
      setError(t('errors.loginRequired'));
      return;
    }

    if (!displayName.trim()) {
      setError(t('errors.displayNameRequired'));
      return;
    }

    if (!username.trim()) {
      setError(t('errors.usernameRequired'));
      return;
    }

    // アバター未設定で初回登録の場合、確認ダイアログを表示
    const hasAvatar = avatarBlob || (avatarPreview && !avatarPreview.startsWith('blob:'));
    if (!hasAvatar && !isProfileExisting && !showAvatarConfirm) {
      setShowAvatarConfirm(true);
      return;
    }
    setShowAvatarConfirm(false);

    setIsLoading(true);

    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      setError(t('errors.usernameInvalidChars'));
      setIsLoading(false);
      return;
    }

    if (username.length < 3 || username.length > 20) {
      setError(t('errors.usernameLength'));
      setIsLoading(false);
      return;
    }

    try {
      const isUnique = await isUsernameUnique(username);
      if (!isUnique) {
        setError(t('errors.usernameTaken'));
        setIsLoading(false);
        return;
      }

      let avatarUrl: string | null = null;
      if (avatarBlob) {
        avatarUrl = await handleUploadAvatar();
      } else if (avatarPreview && !avatarPreview.startsWith('blob:')) {
        // Keep existing avatar URL if no new image was selected
        avatarUrl = avatarPreview;
      }

      // 保存前に再度サニタイズ（二重チェック）
      const sanitizedDisplayName = sanitizeInput(displayName.trim(), displayNameSanitizeOptions);
      const sanitizedUsername = sanitizeInput(username.trim(), usernameSanitizeOptions);
      const sanitizedBio = sanitizeInput(bio.trim(), bioSanitizeOptions);

      // Firebase Authのプロフィールを更新
      await updateProfile(user, {
        displayName: sanitizedDisplayName,
        photoURL: avatarUrl || undefined
      });

      const userData: Record<string, any> = {
        uid: user.uid,
        displayName: sanitizedDisplayName,
        username: sanitizedUsername,
        bio: sanitizedBio,
        updatedAt: new Date()
      };

      if (avatarUrl) {
        userData.avatarUrl = avatarUrl;
      }
      if (user.email) {
        userData.email = user.email;
      }

      const userDocRef = doc(db, 'users', user.uid);

      if (isProfileExisting) {
        await updateDoc(userDocRef, userData);
      } else {
        // 新規ユーザー
        userData.createdAt = new Date();
        userData.followers = [];
        userData.following = [];
        userData.fans = [];
        userData.isCreator = false;
        userData.isVerified = false;
        userData.settings = {};

        await setDoc(userDocRef, userData);

        // users_by_username にエントリを作成
        await setDoc(doc(db, 'users_by_username', sanitizedUsername), {
          uid: user.uid
        });

        // 残高 doc の初期付与。
        // ⚠**クライアントからは書かない。** 枚数の決定と書き込みはサーバーが持つ
        //   (figryne functions/ticketBalance.ts の INITIAL_FREE_TICKETS が唯一の出所)。
        //   以前はここでクライアントが 500 を書いており、rules が `allow write: 本人` だった
        //   ため、コンソールから任意の残高を書き込めた。
        //   orifoli は figryne と同じ Firebase プロジェクトを使うので rules も共通。
        await httpsCallable(getFunctions(undefined, 'asia-northeast1'), 'ensureTicketBalance')({});

        // 初回登録完了トーストメッセージを保存
        sessionStorage.setItem('pendingToast', t('toast.registrationComplete'));
      }

      // 成功後、元のページに戻る
      const from = location.state?.from?.pathname || '/';
      if (location.state?.openWizardAfterSettings) {
        // invite画面等から来た場合: 戻ってウィザードを開く
        sessionStorage.setItem('openWizardOnLoad', JSON.stringify({
          defaultCategory: location.state?.defaultCategory || 'original',
        }));
        sessionStorage.removeItem('afterAuthAction');
      }
      navigate(from);
    } catch (error: any) {
      console.error('Error saving profile:', error);
      setError(t('errors.saveFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleBack = () => {
    // 表示名が未入力の場合はバリデーションエラー
    if (!displayName.trim()) {
      setError(t('errors.displayNameRequired'));
      return;
    }

    // ユーザー名が未入力の場合はバリデーションエラー
    if (!username.trim()) {
      setError(t('errors.usernameRequired'));
      return;
    }

    if (isProfileExisting) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  if (authLoading || isInitializing) {
    return (
      <div className="fixed inset-0 z-50 min-h-screen bg-[#f8f7f8] flex items-center justify-center">
        <div className="text-center">
          <img
            src={logoImage}
            alt="ORIFOLI"
            className="h-12 mx-auto mb-6"
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-gray-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f7f8]">
      {/* Crop Modal */}
      {cropImageSrc && (
        <CropImageModal
          isOpen={isCropModalOpen}
          imageSrc={cropImageSrc}
          onClose={handleCropModalClose}
          onCropComplete={handleCropComplete}
        />
      )}

      {/* ヘッダー */}
      <header className="sticky top-0 z-50 h-14 bg-white border-b border-gray-200">
        <div className="h-full flex items-center justify-between px-4">
          <button
            onClick={handleBack}
            className="text-gray-600 hover:text-gray-900 p-2 -ml-2"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <span className="font-semibold text-gray-900">
            {isProfileExisting ? t('title.edit') : t('title.setup')}
          </span>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="px-4 py-1.5 bg-black hover:bg-gray-800 text-white font-semibold text-sm rounded-full disabled:opacity-50 transition-colors"
          >
            {isLoading ? t('saving') : t('save')}
          </button>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="pb-20">
        <div className="px-5 pt-8">
          {/* 説明文 */}
          <div className="px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl text-sm text-gray-600 leading-relaxed mb-6">
            {t('description')}
          </div>

          {/* アバター */}
          <div className="mb-6">
            <label className="block text-sm font-bold text-gray-700 mb-2">
              {t('avatar.label')}
            </label>
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20 flex-shrink-0">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarChange}
                  accept="image/jpeg,image/png"
                  className="hidden"
                  disabled={isLoading}
                />
                <div
                  className="w-20 h-20 rounded-full overflow-hidden border-4 border-white bg-white shadow-lg cursor-pointer"
                  onClick={handleAvatarClick}
                >
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-300 flex items-center justify-center text-white text-xl font-bold">
                      {displayName?.charAt(0).toUpperCase() || '?'}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleAvatarClick}
                  className="absolute bottom-0 right-0 w-6 h-6 bg-black rounded-full flex items-center justify-center shadow-md"
                  disabled={isLoading}
                >
                  <CameraIcon className="w-3.5 h-3.5 text-white" />
                </button>
              </div>
              <div>
                <button
                  type="button"
                  onClick={handleAvatarClick}
                  className="text-sm text-gray-700 font-medium hover:text-gray-900 transition-colors"
                  disabled={isLoading}
                >
                  {t('avatar.tapToChange')}
                </button>
                <p className="text-xs text-gray-400 mt-0.5">
                  {t('avatar.requirements')}
                </p>
              </div>
            </div>
          </div>

          {/* エラーメッセージ */}
          {error && (
            <div className="mb-4 px-4 py-3 bg-red-100 border border-red-300 text-red-700 rounded-xl text-sm">
              {error}
            </div>
          )}

          {/* フォームフィールド */}
          <div className="space-y-5">
            {/* 表示名 */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                {t('fields.displayName.label')}<span className="text-red-500 ml-1">{t('fields.displayName.required')}</span>
              </label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(sanitizeInput(e.target.value, displayNameSanitizeOptions))}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder={t('fields.displayName.placeholder')}
                maxLength={MAX_DISPLAY_NAME_LENGTH}
                disabled={isLoading}
              />
              <div className="flex justify-between mt-1">
                <p className="text-gray-500 text-xs">
                  {t('fields.displayName.hint')}
                </p>
                <span className={`text-xs ${displayName.length >= MAX_DISPLAY_NAME_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                  {displayName.length}/{MAX_DISPLAY_NAME_LENGTH}
                </span>
              </div>
            </div>

            {/* ユーザー名 */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                {t('fields.username.label')}<span className="text-red-500 ml-1">{t('fields.username.required')}</span>
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500">
                  @
                </span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(sanitizeInput(e.target.value, usernameSanitizeOptions))}
                  className="w-full pl-8 pr-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-900 focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder={t('fields.username.placeholder')}
                  disabled={isLoading}
                />
              </div>
              <p className="text-gray-500 text-xs mt-1">
                {t('fields.username.hint')}
              </p>
            </div>

            {/* 自己紹介 */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1.5">
                {t('fields.bio.label')}
              </label>
              <textarea
                value={bio}
                onChange={(e) => setBio(sanitizeInput(e.target.value, bioSanitizeOptions))}
                className="w-full px-4 py-2.5 bg-white border border-gray-300 rounded-xl text-gray-900 min-h-[100px] resize-none focus:ring-2 focus:ring-black focus:border-transparent"
                placeholder={t('fields.bio.placeholder')}
                maxLength={MAX_BIO_LENGTH}
                disabled={isLoading}
              />
              <div className="flex justify-end mt-1">
                <span className={`text-xs ${bio.length >= MAX_BIO_LENGTH ? 'text-red-500' : 'text-gray-400'}`}>
                  {bio.length}/{MAX_BIO_LENGTH}
                </span>
              </div>
            </div>
          </div>

          {/* アバター未設定の確認 */}
          {showAvatarConfirm && (
            <div className="mt-6 px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl">
              <p className="text-sm text-gray-700 font-medium mb-1">
                {t('avatar.noAvatarTitle')}
              </p>
              <p className="text-xs text-gray-500 mb-4">
                {t('avatar.noAvatarDescription')}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleAvatarClick}
                  className="flex-1 py-2.5 text-sm font-semibold bg-black hover:bg-gray-800 text-white rounded-xl transition-colors"
                >
                  {t('avatar.setAvatar')}
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 text-sm font-semibold bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-xl transition-colors"
                >
                  {t('avatar.continueWithout')}
                </button>
              </div>
            </div>
          )}

          {/* 保存ボタン */}
          {!showAvatarConfirm && (
            <div className="mt-8">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 px-4 bg-black hover:bg-gray-800 text-white font-semibold text-sm rounded-xl transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                    {t('saving')}
                  </div>
                ) : (
                  t('save')
                )}
              </button>
            </div>
          )}

          {/* ログアウトリンク */}
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={handleLogout}
              disabled={isLoading}
              className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors disabled:opacity-50"
            >
              <LogOutIcon className="w-4 h-4" />
              {t('logout')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SettingsPage;
